// project imports
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import type { PayloadAction } from '@reduxjs/toolkit';
import type { RootState } from 'store';
import apiClient from 'utils/apiClient';

interface AccountHistoryState {
    accountId?: string
    histories: AccountHistoryProps[]
    finished: boolean
    lastCreatedAt?: number
}

export const initialState: AccountHistoryState = {
    histories: [],
    finished: false
};

interface AccountHistoryProps {
    accountId: string
    ip: string
    country: string
    traceId: string
    userAgent: string
    platform: string
    appVersion: string
    os: string
    osVersion: string
    platformDevice: string
    platformEnvironment: string
    provider: string
    type: string
    createdAt: number
    success: boolean
    error: string
}

let pending = false;

export const searchAccountHistories = createAsyncThunk<AccountHistoryProps[], string, { state: RootState }>(
    'accountHistory/search',
    async (id: string, thunkAPI) => {
        if (!id) return;
        const state = thunkAPI.getState();
        const { lastCreatedAt, finished } = state.accountHistory;

        if (finished || pending) return;

        pending = true;

        let url = `accounts/${id}/histories`;
        if (lastCreatedAt) url += `?createdAt=${lastCreatedAt}`

        try {
            const response = await apiClient.get<AccountHistoryProps[]>(url);
            return response.data;
        } finally {
            pending = false;
        }
    }
)

const accountHistorySlice = createSlice({
    name: "accountHistory",
    initialState,
    extraReducers: (builder) => {
        builder
            .addCase(searchAccountHistories.fulfilled, (state, action) => {
                if (!action.payload) return;
                const histories = action.payload;

                state.histories = state.histories.concat(histories);

                const length = histories.length;
                if (length > 0) {
                    const last = histories[length - 1];
                    state.lastCreatedAt = last.createdAt;
                }

                if (length === 0) state.finished = true;
            })
    },
    reducers: {
        refresh: () => {
            pending = false;
            return initialState;
        },
    }
})

export const {
    refresh
} = accountHistorySlice.actions;
export default accountHistorySlice.reducer;
